import React, { useEffect, useRef, useState } from "react";
import { authStore } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "../components/ui/sonner";
import { X, Minus, Save, Download, Loader2, FileSignature, Eraser, Maximize2, Mail, QrCode, History, Copy, ExternalLink, RefreshCw, Pencil, Trash2, Eye, Paperclip, Sparkles, MessageSquare } from "lucide-react";
import { fmtDT } from "../lib/date";

const AUTO_HINTS = {
  price: [["totalSf", "总建筑面积 Total sf"], ["totalDisplay", "合同总价(计入附加项) Effective Total"]],
  payment: [["pct20", "付款3-6 (20%)"], ["pay7Net", "付款7 净额"], ["pay8Net", "付款8 净额"]],
  project: [["totalSf", "总建筑面积 Total sf"]],
};

function SignaturePad({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext("2d"); ctx.clearRect(0, 0, cv.width, cv.height);
    if (value) { const img = new window.Image(); img.onload = () => ctx.drawImage(img, 0, 0, cv.width, cv.height); img.src = value; }
  }, [value]);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: ((t.clientX - r.left) / r.width) * ref.current.width, y: ((t.clientY - r.top) / r.height) * ref.current.height }; };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke(); last.current = p; };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, ref.current.width, ref.current.height); onChange(""); };
  return (
    <div className="flex flex-col">
      <span className="text-[12px] text-slate-600 mb-0.5">{label}</span>
      <canvas ref={ref} width={280} height={80} data-testid={`${testid}-canvas`}
        className="w-full h-[80px] bg-white border border-slate-300 rounded-md touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button data-testid={`${testid}-clear`} onClick={clear} className="text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-0.5 mt-0.5 self-end"><Eraser className="h-3 w-3" />清除</button>
    </div>
  );
}

export default function ContractEditorLegacy({ leadId, leadBranch, api, zh, canManagePresets, onClose, prefillEstimate }) {
  const [groups, setGroups] = useState([]);
  const [fields, setFields] = useState({});
  const [auto, setAuto] = useState({});
  const [signatures, setSignatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeGroup, setActiveGroup] = useState("header");
  const [shareModal, setShareModal] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [contractMeta, setContractMeta] = useState({});
  const [estimateId, setEstimateId] = useState(null);
  const [nudging, setNudging] = useState(false);
  const [showTrail, setShowTrail] = useState(false);
  const [conceptAtt, setConceptAtt] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [featBusy, setFeatBusy] = useState(false);
  const [sendingLink, setSendingLink] = useState(null);

  const computeAuto = (f) => {
    const num = (s) => parseFloat(String(s ?? "").replace(/[^\d.]/g, "")) || 0;
    const money = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const base = num(f.totalPrice), add = num(f.additionSf), adu = num(f.aduSf);
    const sel = Array.isArray(f.addOns) ? f.addOns : [];
    const opt = (groups.find((g) => g.group === "addons")?.options) || [];
    const priceFor = (k) => num(addonPrices[k] ?? opt.find((o) => o.key === k)?.price ?? 0);
    const addonSum = f.includeAddonsInTotal ? sel.reduce((t, k) => t + priceFor(k), 0) : 0;
    const total = base + addonSum;
    const ded7 = num(f.pay1Amount) + num(f.pay2Amount);
    const ded8 = num(f.pay9Amount);
    setAuto({ totalSf: Math.round(add + adu).toLocaleString("en-US"), totalDisplay: money(total), pct20: money(total * 0.2), pay7Net: money(total * 0.1 - ded7), pay8Net: money(total * 0.1 - ded8) });
  };

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([api.get("/crm/contract-template"), api.get(`/crm/leads/${leadId}/contract`)]);
        setGroups(t.data.groups || []);
        setFields(c.data.fields || {}); setAuto(c.data.auto || {}); setSignatures(c.data.signatures || {});
        setContractMeta({ ownerViewedAt: c.data.ownerViewedAt, ownerLastViewedAt: c.data.ownerLastViewedAt, ownerViewCount: c.data.ownerViewCount || 0, lastSentAt: c.data.lastSentAt, ownerViews: c.data.ownerViews || [], lastViewNudgeAt: c.data.lastViewNudgeAt });
        setEstimateId(c.data.estimateId || null);
        setConceptAtt(c.data.conceptAttachment || null);
        refreshPreview();
      } catch { toast.error(zh ? "加载合同失败" : "Load failed"); }
      setLoading(false);
    })();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [leadId]); // eslint-disable-line

  const prefillDone = useRef(false);
  useEffect(() => {
    if (!loading && prefillEstimate && !prefillDone.current) {
      prefillDone.current = true;
      applyEstimate();
    }
  }, [loading, prefillEstimate]); // eslint-disable-line

  const setField = (k, v) => { setFields((f) => { const nf = { ...f, [k]: v }; computeAuto(nf); return nf; }); setDirty(true); };
  const regenFeatures = () => {
    const num = (s) => parseFloat(String(s ?? "").replace(/[^\d.]/g, "")) || 0;
    const addN = num(fields.additionSf), aduN = num(fields.aduSf);
    const fmt = (n) => n.toLocaleString("en-US");
    const totalSf = fmt(Math.round(addN + aduN));
    const enTxt = `The project involves the construction of a ${fmt(addN)} square foot addition to the existing main residence and a new ${fmt(aduN)} square foot Accessory Dwelling Unit (ADU), for a total construction area of approximately ${totalSf} square feet.`;
    const zhTxt = `本项目包括对现有主屋进行约 ${fmt(addN)}平方英尺扩建（Addition），以及新建一栋约 ${fmt(aduN)}平方英尺附属住宅单元（ADU），总建筑面积约 ${totalSf}平方英尺。`;
    setFields((f) => ({ ...f, featuresEn: enTxt, featuresZh: zhTxt })); setDirty(true);
    toast.success(zh ? "已按当前面积重写 1.1 项目特征" : "Regenerated project features");
  };
  const setSig = (role, v) => { setSignatures((s) => ({ ...s, [role]: v })); setDirty(true); };
  const extractCity = (addr) => {
    const parts = String(addr || "").split(",").map((p) => p.trim()).filter(Boolean);
    while (parts.length && /^[A-Za-z]{2}\s*\d{0,5}(-\d{4})?$|^\d{5}(-\d{4})?$/.test(parts[parts.length - 1])) parts.pop();
    return parts.length ? parts[parts.length - 1] : "";
  };
  const smartName = () => {
    const num = (s) => parseFloat(String(s ?? "").replace(/[^\d.]/g, "")) || 0;
    const fmt = (n) => n.toLocaleString("en-US");
    const add = num(fields.additionSf), adu = num(fields.aduSf);
    const shortMap = { detached: "独立ADU", attached: "附属ADU", jadu: "JADU", garage: "车库改造", addition: "主屋扩建" };
    const t = String(fields.aduType || "").trim();
    let typ;
    if (shortMap[t]) {
      const sf = t === "addition" ? add : (adu > 0 ? adu : add);
      typ = sf > 0 ? `${shortMap[t]} ${fmt(Math.round(sf))}sf` : shortMap[t];
    } else if (add > 0 && adu > 0) typ = `扩建+ADU ${fmt(Math.round(add + adu))}sf`;
    else if (adu > 0) typ = `ADU ${fmt(Math.round(adu))}sf`;
    else if (add > 0) typ = `扩建 ${fmt(Math.round(add))}sf`;
    else typ = "ADU";
    const city = extractCity(fields.ownerAddress);
    const name = String(fields.ownerName || "").trim();
    const pn = [name, city, typ].filter(Boolean).join(" · ");
    setField("projectName", pn);
    toast.success(zh ? "已智能生成项目名称" : "Project name generated");
  };
  const applyEstimateFeatures = async () => {
    setFeatBusy(true);
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/contract/estimate-defaults`);
      if (!data.featuresEn && !data.featuresZh) { toast.warning(zh ? "报价单暂无可带入的明细" : "No estimate details"); }
      else { setFields((f) => ({ ...f, featuresEn: data.featuresEn || f.featuresEn, featuresZh: data.featuresZh || f.featuresZh })); setDirty(true); toast.success(zh ? "已带入报价明细（户型/层高/特殊配置）到项目特征" : "Features from estimate"); }
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "带入失败，请先在报价单保存报价" : "Failed")); }
    setFeatBusy(false);
  };
  const [featPresets, setFeatPresets] = useState([]);
  const pBranch = leadBranch || "";
  const branchTag = (b) => (!b ? (zh ? "通用" : "Global") : ({ la: zh ? "洛杉矶" : "LA", norcal: zh ? "北加" : "NorCal", sd: zh ? "圣地亚哥" : "SD", hq: "HQ" }[b] || b));
  const canEditPreset = (p) => (p.branch || "") === pBranch;
  const putFeatPresets = async (ownList) => {
    const { data } = await api.put("/crm/contract-feature-presets", { branch: pBranch, presets: ownList.map((x) => ({ name: x.name, en: x.en || "", zh: x.zh || "" })) });
    setFeatPresets(data.presets || []);
  };
  const applyFeaturePreset = (p) => { setFields((f) => ({ ...f, featuresEn: p.en || "", featuresZh: p.zh || "" })); setDirty(true); toast.success(zh ? `已套用特征模板「${p.name}」` : `Applied ${p.name}`); };
  const saveFeaturePreset = async () => {
    const name = window.prompt(zh ? `为当前项目特征描述起个模板名（保存到「${branchTag(pBranch)}」）` : "Feature preset name");
    if (!name || !name.trim()) return;
    const own = featPresets.filter(canEditPreset).filter((x) => x.name !== name.trim());
    try { await putFeatPresets([...own, { name: name.trim(), en: fields.featuresEn || "", zh: fields.featuresZh || "" }]); toast.success(zh ? "特征模板已保存" : "Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
  };
  const renameFeaturePreset = async (p) => {
    const name = window.prompt(zh ? "重命名特征模板" : "Rename preset", p.name);
    if (!name || !name.trim() || name.trim() === p.name) return;
    const own = featPresets.filter(canEditPreset).map((x) => x.name === p.name ? { ...x, name: name.trim() } : x);
    try { await putFeatPresets(own); toast.success(zh ? "已重命名" : "Renamed"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
  };
  const deleteFeaturePreset = async (p) => {
    if (!window.confirm(zh ? `确定删除特征模板「${p.name}」？` : `Delete preset "${p.name}"?`)) return;
    const own = featPresets.filter(canEditPreset).filter((x) => x.name !== p.name);
    try { await putFeatPresets(own); toast.success(zh ? "已删除" : "Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
  };
  const applyIntake = async () => {
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/contract/lead-defaults`);
      const upd = data.fields || {};
      setFields((f) => { const nf = { ...f, ...upd }; computeAuto(nf); return nf; });
      setDirty(true);
      toast.success(zh ? "已用最新调研覆盖业主信息/面积" : "Overwritten from intake");
    } catch { toast.error(zh ? "覆盖失败" : "Failed"); }
  };
  const applyEstimate = async () => {
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/contract/estimate-defaults`);
      const upd = data.fields || {};
      setFields((f) => { const nf = { ...f, ...upd }; computeAuto(nf); return nf; });
      setDirty(true);
      const n = (data.matchedAddons || []).length;
      if (data.estimateId) setEstimateId(data.estimateId);
      toast.success(zh ? `已带入报价：合同总价、ADU 面积${n ? `、${n} 项附加配置` : ""}` : `Prefilled from estimate${n ? ` (+${n} add-ons)` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "带入报价失败" : "Failed")); }
  };

  // 【SYS-007】旧模板合同保存后同样广播 crm-lead-changed：业主邮箱可能就是在
  // ownerEmail 字段里补的，列表页「未发送提醒」与侧栏角标据此即时重算。
  const persist = async () => {
    await api.put(`/crm/leads/${leadId}/contract`, { fields, signatures, estimateId });
    setDirty(false);
    window.dispatchEvent(new Event("crm-lead-changed"));
  };

  const refreshPreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.get(`/crm/leads/${leadId}/contract/pdf?t=${Date.now()}`, { responseType: "blob" });
      setPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(res.data); });
    } catch { /* ignore */ }
    setPreviewing(false);
  };

  const save = async () => {
    setSaving(true);
    try { await persist(); toast.success(zh ? "合同已保存" : "Saved"); loadVersions(); await refreshPreview(); }
    catch { toast.error(zh ? "保存失败" : "Save failed"); }
    setSaving(false);
  };

  const saveRefresh = async () => { if (dirty) { try { await persist(); } catch { /* */ } } await refreshPreview(); };

  const exportPdf = async () => {
    setExporting(true);
    try {
      if (dirty) await persist();
      const res = await api.get(`/crm/leads/${leadId}/contract/pdf?dl=1`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `contract_${leadId}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success(zh ? "已导出 PDF" : "Exported");
    } catch { toast.error(zh ? "导出失败" : "Export failed"); }
    setExporting(false);
  };

  const loadVersions = async () => { try { const { data } = await api.get(`/crm/leads/${leadId}/contract/versions`); setVersions(data.versions || []); } catch { /* */ } };
  const downloadVersion = async (versionId) => {
    try { const res = await api.get(`/crm/leads/${leadId}/contract/pdf?versionId=${versionId}`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `contract_${leadId}_${versionId}.pdf`; a.click(); URL.revokeObjectURL(url); }
    catch { toast.error(zh ? "下载失败" : "Download failed"); }
  };

  const emailOwner = async () => {
    setEmailing(true);
    try {
      if (dirty) await persist();
      const { data } = await api.post(`/crm/leads/${leadId}/contract/send-email`, {});
      if (data.ok) toast.success(zh ? `已发送至业主 ${data.to}` : `Sent to ${data.to}`);
      else toast.warning(zh ? `邮件未送达(${data.info || "邮箱服务未配置"})，请改用「业主签署」链接` : `Not delivered: ${data.info}`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败，请确认业主邮箱已填写" : "Send failed")); }
    setEmailing(false);
  };

  const nudgeView = async () => {
    setNudging(true);
    try {
      if (dirty) await persist();
      const { data } = await api.post(`/crm/leads/${leadId}/contract/nudge-view`, { baseUrl: window.location.origin });
      if (data.ok) toast.success(zh ? `已催看业主（${(data.channels || []).map((c) => c === "email" ? "邮件" : "短信").join("、")}）` : `Reminder sent (${(data.channels || []).join(", ")})`);
      else toast.warning(zh ? "邮件/短信通道未配置，未能发送。可复制签署链接手动发给业主。" : "Email/SMS not configured");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "催看失败，请确认业主邮箱/手机已填写" : "Failed")); }
    setNudging(false);
  };

  const shareForSign = async () => {
    setSharing(true);
    try {
      if (dirty) await persist();
      const { data } = await api.post(`/crm/leads/${leadId}/contract/share`);
      const link = `${window.location.origin}/contract-sign/${data.token}`;
      let qr = null;
      try { const res = await api.get(`/crm/proposal-qr?data=${encodeURIComponent(link)}`, { responseType: "blob" }); qr = URL.createObjectURL(res.data); } catch { /* */ }
      setShareModal({ url: link, qr });
    } catch { toast.error(zh ? "生成链接失败" : "Failed"); }
    setSharing(false);
  };

  const sendLink = async (channel) => {
    setSendingLink(channel);
    try {
      if (dirty) await persist();
      const { data } = await api.post(`/crm/leads/${leadId}/contract/send-link`, { channel, baseUrl: window.location.origin });
      if (data.ok) toast.success(zh ? `已通过${(data.channels || []).map((c) => c === "email" ? "邮件" : "短信").join("、")}发送签署链接给业主` : `Sent via ${(data.channels || []).join(", ")}`);
      else toast.warning(zh ? `未能发送：${(data.errors || []).join("；")}。可复制下方链接手动发给业主。` : `Not sent: ${(data.errors || []).join("; ")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败，请确认业主邮箱/手机已填写" : "Failed")); }
    setSendingLink(null);
  };

  const tryClose = () => { if (dirty && !window.confirm(zh ? "有未保存的修改，确定退出？" : "Unsaved changes. Exit?")) return; onClose(); };

  const attachConcept = async () => {
    setAttaching(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/contract/attach-concept`);
      setConceptAtt(data.conceptAttachment);
      toast.success(zh ? "已引用客户确认的概念方案，签约时随合同带入" : "Signed concept attached");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "引用失败：请先完成概念方案并让客户确认" : "Failed")); }
    setAttaching(false);
  };
  const conceptUrl = conceptAtt ? `${api.defaults.baseURL}/files/${conceptAtt.fileId}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}` : null;

  const allGroups = groups;
  const activeGroupObj = groups.find((g) => g.group === activeGroup);
  const activeFields = activeGroupObj?.fields || [];
  const addOns = Array.isArray(fields.addOns) ? fields.addOns : [];
  const toggleAddon = (k) => { const cur = new Set(addOns); cur.has(k) ? cur.delete(k) : cur.add(k); setField("addOns", Array.from(cur)); };
  const [addonPrices, setAddonPrices] = useState({});
  const [pricesDirty, setPricesDirty] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [presets, setPresets] = useState([]);
  useEffect(() => {
    const bq = pBranch ? `?branch=${encodeURIComponent(pBranch)}` : "";
    api.get("/crm/contract-addon-prices").then(({ data }) => setAddonPrices(data.prices || {})).catch(() => {});
    api.get(`/crm/contract-addon-presets${bq}`).then(({ data }) => setPresets(data.presets || [])).catch(() => {});
    api.get(`/crm/contract-feature-presets${bq}`).then(({ data }) => setFeatPresets(data.presets || [])).catch(() => {});
  }, []); // eslint-disable-line
  const priceOf = (k) => addonPrices[k] ?? (activeGroupObj?.options || []).find((o) => o.key === k)?.price ?? "";
  const parseNum = (s) => { const m = String(s || "").match(/[\d,]+(?:\.\d+)?/); return m ? parseFloat(m[0].replace(/,/g, "")) || 0 : 0; };
  const addonSubtotal = addOns.reduce((t, k) => t + parseNum(priceOf(k)), 0);
  const money = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const putAddonPresets = async (ownList) => {
    const { data } = await api.put("/crm/contract-addon-presets", { branch: pBranch, presets: ownList.map((x) => ({ name: x.name, keys: x.keys || [] })) });
    setPresets(data.presets || []);
  };
  const applyPreset = (p) => { setField("addOns", Array.from(new Set(p.keys || []))); toast.success(zh ? `已套用「${p.name}」` : `Applied ${p.name}`); };
  const savePreset = async () => {
    const name = window.prompt(zh ? `为当前勾选的附加配置组合起个名字（保存到「${branchTag(pBranch)}」）` : "Preset name");
    if (!name || !name.trim()) return;
    const own = presets.filter(canEditPreset).filter((x) => x.name !== name.trim());
    try { await putAddonPresets([...own, { name: name.trim(), keys: addOns }]); toast.success(zh ? "模板已保存" : "Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
  };
  const renamePreset = async (p) => {
    const name = window.prompt(zh ? "重命名配置模板" : "Rename preset", p.name);
    if (!name || !name.trim() || name.trim() === p.name) return;
    const own = presets.filter(canEditPreset).map((x) => x.name === p.name ? { ...x, name: name.trim() } : x);
    try { await putAddonPresets(own); toast.success(zh ? "已重命名" : "Renamed"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
  };
  const deletePreset = async (p) => {
    if (!window.confirm(zh ? `确定删除模板「${p.name}」？` : `Delete preset "${p.name}"?`)) return;
    const own = presets.filter(canEditPreset).filter((x) => x.name !== p.name);
    try { await putAddonPresets(own); toast.success(zh ? "已删除" : "Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
  };
  const [dragPresetIdx, setDragPresetIdx] = useState(null);
  const reorderPresets = async (from, to) => {
    if (from == null || to == null || from === to) return;
    const own = presets.filter(canEditPreset);
    const next = [...own];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    try { await putAddonPresets(next); toast.success(zh ? "已保存排序" : "Order saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "排序保存失败" : "Failed")); }
  };
  const setPrice = (k, v) => { setAddonPrices((p) => ({ ...p, [k]: v })); setPricesDirty(true); };
  const saveStdPrices = async () => {
    setSavingPrices(true);
    try { await api.put("/crm/contract-addon-prices", { prices: addonPrices }); toast.success(zh ? "标准单价已保存" : "Prices saved"); setPricesDirty(false); if (dirty) await persist(); await refreshPreview(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    setSavingPrices(false);
  };

  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[80] bg-white rounded-xl shadow-2xl border border-slate-200 px-4 py-3 flex items-center gap-3" data-testid="contract-editor-minimized">
        <FileSignature className="h-5 w-5 text-brand-500" />
        <span className="text-sm font-medium text-slate-700">{zh ? "合同编辑器" : "Contract"} {dirty && <span className="text-red-500">•</span>}</span>
        <button data-testid="contract-restore" onClick={() => setMinimized(false)} className="text-slate-500 hover:text-indigo-600"><Maximize2 className="h-4 w-4" /></button>
        <button data-testid="contract-minimized-close" onClick={tryClose} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm flex flex-col" data-testid="contract-editor">
      <div className="mobile-safe-topbar bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between shadow-sm flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-brand-500" />
          <h2 className="text-base font-bold text-slate-800">{zh ? "建筑施工合同 · 编辑" : "Construction Contract"}</h2>
          {dirty && <span className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{zh ? "未保存" : "Unsaved"}</span>}
          {contractMeta.ownerViewedAt && !signatures.owner && (
            <span data-testid="contract-owner-viewed" className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Eye className="h-3 w-3" />{zh ? "业主已查看" : "Owner viewed"} {(contractMeta.ownerLastViewedAt || contractMeta.ownerViewedAt || "").slice(5, 10)}{contractMeta.ownerViewCount > 1 ? `（${contractMeta.ownerViewCount}${zh ? "次" : "x"}）` : ""}</span>
          )}
          {contractMeta.lastSentAt && !contractMeta.ownerViewedAt && !signatures.owner && (
            <span data-testid="contract-owner-notviewed" className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">{zh ? "业主尚未查看" : "Not yet viewed"}</span>
          )}
          {conceptAtt && (
            <a href={conceptUrl} target="_blank" rel="noreferrer" data-testid="contract-concept-chip" className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1 hover:bg-emerald-100"><Paperclip className="h-3 w-3" />{zh ? "已引用概念方案" : "Concept attached"}</a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="contract-apply-intake" onClick={applyIntake} className="h-8 border-indigo-200 text-indigo-700"><RefreshCw className="h-3.5 w-3.5 mr-1" />{zh ? "带入最新调研" : "From intake"}</Button>
          <Button size="sm" variant="outline" data-testid="contract-apply-estimate" onClick={applyEstimate} className="h-8 border-red-200 text-red-700"><RefreshCw className="h-3.5 w-3.5 mr-1" />{zh ? "带入报价" : "From estimate"}</Button>
          <Button size="sm" variant="outline" data-testid="contract-attach-concept" onClick={attachConcept} disabled={attaching} className={`h-8 ${conceptAtt ? "border-emerald-300 text-emerald-700" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>{attaching ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Paperclip className="h-3.5 w-3.5 mr-1" />}{conceptAtt ? (zh ? "更新概念方案引用" : "Re-attach concept") : (zh ? "引用已确认概念方案" : "Attach concept")}</Button>
          <Button size="sm" variant="outline" data-testid="contract-save" onClick={save} disabled={saving} className="h-8 border-slate-200">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存" : "Save"}</Button>
          <div className="relative">
            <Button size="sm" variant="outline" data-testid="contract-versions-btn" onClick={() => { setShowVersions((v) => !v); if (!showVersions) loadVersions(); }} className="h-8 border-slate-200"><History className="h-3.5 w-3.5 mr-1" />{zh ? "历史版本" : "Versions"}</Button>
            {showVersions && (
              <div className="absolute right-0 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-xl z-10 max-h-80 overflow-y-auto" data-testid="contract-versions-panel">
                <p className="text-[11px] text-slate-400 px-3 pt-2">{zh ? "每次保存自动留档，可下载回看/对比" : "Auto-saved each save"}</p>
                {versions.length === 0 && <p className="text-[12px] text-slate-400 px-3 py-3">{zh ? "暂无历史版本" : "No versions"}</p>}
                {versions.map((v, i) => (
                  <div key={v.versionId} data-testid={`contract-version-${i}`} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 border-t border-slate-50">
                    <div className="text-[12px] text-slate-600"><span className="font-medium">{i === 0 ? (zh ? "最新" : "Latest") : `v${versions.length - i}`}</span> · {fmtDT(v.at)}<br /><span className="text-[11px] text-slate-400">{v.by || ""}</span></div>
                    <button data-testid={`contract-version-dl-${i}`} onClick={() => downloadVersion(v.versionId)} className="text-[11px] text-indigo-600 hover:underline flex items-center gap-0.5"><Download className="h-3 w-3" />PDF</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" data-testid="contract-share-sign" onClick={shareForSign} disabled={sharing} className="h-8 border-violet-200 text-violet-700 hover:bg-violet-50">{sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <QrCode className="h-3.5 w-3.5 mr-1" />}{zh ? "业主签署" : "Owner sign"}</Button>
          <Button size="sm" variant="outline" data-testid="contract-email-owner" onClick={emailOwner} disabled={emailing} className="h-8 border-slate-200">{emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}{zh ? "发业主" : "Email"}</Button>
          {contractMeta.lastSentAt && !signatures.owner && (
            <Button size="sm" variant="outline" data-testid="contract-nudge-view" onClick={nudgeView} disabled={nudging} className={`h-8 ${contractMeta.ownerViewedAt ? "border-slate-200" : "border-blue-300 text-blue-700 hover:bg-blue-50"}`}>{nudging ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}{zh ? "催业主查看" : "Nudge view"}</Button>
          )}
          <Button size="sm" data-testid="contract-export-pdf" onClick={exportPdf} disabled={exporting} className="h-8 bg-brand-500 hover:bg-brand-600 text-white">{exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}{zh ? "导出 PDF" : "Export"}</Button>
          <button data-testid="contract-minimize" onClick={() => setMinimized(true)} title={zh ? "最小化" : "Minimize"} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Minus className="h-4 w-4" /></button>
          <button data-testid="contract-close" onClick={tryClose} title={zh ? "退出" : "Close"} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {shareModal && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" data-testid="contract-share-modal" onClick={() => setShareModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-slate-800">{zh ? "业主在线签署" : "Owner e-Sign"}</h3><button data-testid="contract-share-close" onClick={() => setShareModal(null)}><X className="h-5 w-5 text-slate-400" /></button></div>
            {shareModal.qr && <img src={shareModal.qr} alt="QR" data-testid="contract-share-qr" className="w-48 h-48 mx-auto rounded-lg border border-slate-100" />}
            <p className="text-[12px] text-slate-500 mt-2">{zh ? "业主扫码或点击链接，在手机上直接签署；签好自动回填到合同 PDF。" : "Owner scans/opens the link to sign on their phone; auto-fills the PDF."}</p>
            <div className="mt-3 flex items-center gap-1">
              <input readOnly value={shareModal.url} data-testid="contract-share-url" className="flex-1 text-[11px] border border-slate-200 rounded-md px-2 py-1.5 truncate" />
              <Button size="sm" data-testid="contract-share-copy" onClick={() => { try { navigator.clipboard.writeText(shareModal.url).catch(() => {}); } catch { /* */ } toast.success(zh ? "已复制" : "Copied"); }} className="h-8 bg-violet-600 hover:bg-violet-700 text-white"><Copy className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-[11px] text-slate-500 mb-1.5 font-medium">{zh ? "一步发给业主 One-tap send" : "One-tap send to owner"}</p>
              <div className="flex gap-2">
                <Button size="sm" data-testid="contract-send-email" onClick={() => sendLink("email")} disabled={!!sendingLink} className="flex-1 h-9 bg-slate-800 hover:bg-slate-900 text-white">{sendingLink === "email" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}{zh ? "邮件发送" : "Email"}</Button>
                <Button size="sm" data-testid="contract-send-sms" onClick={() => sendLink("sms")} disabled={!!sendingLink} className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white">{sendingLink === "sms" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <MessageSquare className="h-3.5 w-3.5 mr-1" />}{zh ? "短信发送" : "SMS"}</Button>
              </div>
            </div>
            <a href={shareModal.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-[11px] text-indigo-600 underline"><ExternalLink className="h-3 w-3" />{zh ? "预览签署页" : "Preview"}</a>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />{zh ? "加载中…" : "Loading…"}</div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          <div className="w-[400px] shrink-0 bg-white border-r border-slate-200 flex flex-col" data-testid="contract-form-panel">
            <div className="flex flex-wrap gap-1 p-2 border-b border-slate-100">
              {allGroups.map((g) => (
                <button key={g.group} data-testid={`contract-group-${g.group}`} onClick={() => setActiveGroup(g.group)}
                  className={`text-[11px] px-2 py-1 rounded-md ${activeGroup === g.group ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{zh ? g.labelZh : g.labelEn}</button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <p className="text-[11px] text-slate-400">{zh ? "仅可编辑以下红色字段；黑色合同正文与版式 100% 原样保留。20%/10% 付款金额按总价自动计算。" : "Edit only these red fields; black contract text & layout preserved 100%. Payment %s auto-computed."}</p>
              <div data-testid="contract-signature-hint" className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2.5 py-1.5 flex items-start gap-1.5">
                <QrCode className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{zh ? "签名请点右上角『业主签署』，发送链接/二维码给业主，在手机上签字后自动回填到合同 PDF。" : "For signatures, click 'Owner sign' (top-right) to send the owner a link/QR to sign on their phone."}</span>
              </div>
              {activeGroup === "addons" ? (
                <div className="space-y-1.5" data-testid="contract-addons">
                  <p className="text-[12px] text-slate-600">{zh ? "勾选本合同的附加配置项（勾选后按标准单价写入合同 Extra Cost 表 + 项目「工作内容」）：" : "Select add-ons (fill standard price into contract Extra Cost table & project Scope):"}</p>
                  {presets.length > 0 && (() => {
                    const own = presets.filter((p) => (p.branch || "") === pBranch);
                    const glob = pBranch ? presets.filter((p) => (p.branch || "") === "") : [];
                    return (
                    <div className="flex flex-wrap gap-1.5 items-center" data-testid="contract-addon-presets">
                      <span className="text-[11px] text-slate-400 self-center">{zh ? "配置模板：" : "Presets:"}</span>
                      {glob.map((p) => (
                        <span key={`g-${p.name}`} data-testid={`contract-preset-pill-${p.name}`} className="inline-flex items-center rounded-full bg-slate-50 border border-slate-200 overflow-hidden">
                          <button data-testid={`contract-preset-${p.name}`} onClick={() => applyPreset(p)} className="text-[11px] px-2 py-1 text-slate-600 hover:bg-slate-100">{p.name}<span className="ml-1 text-[9px] text-slate-400">{zh ? "通用" : "Global"}</span></button>
                        </span>
                      ))}
                      {own.map((p, i) => (
                        <span key={`o-${p.name}`}
                          draggable={canManagePresets}
                          onDragStart={() => setDragPresetIdx(i)}
                          onDragOver={(e) => { if (canManagePresets) e.preventDefault(); }}
                          onDrop={() => { reorderPresets(dragPresetIdx, i); setDragPresetIdx(null); }}
                          data-testid={`contract-preset-pill-${p.name}`}
                          className={`inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 overflow-hidden ${canManagePresets ? "cursor-grab active:cursor-grabbing" : ""} ${dragPresetIdx === i ? "opacity-50" : ""}`}>
                          <button data-testid={`contract-preset-${p.name}`} onClick={() => applyPreset(p)} className="text-[11px] px-2 py-1 text-indigo-700 hover:bg-indigo-100">{p.name}<span className="ml-1 text-[9px] text-indigo-400">{branchTag(pBranch)}</span></button>
                          {canManagePresets && (
                            <>
                              <button data-testid={`contract-preset-rename-${p.name}`} onClick={() => renamePreset(p)} title={zh ? "重命名" : "Rename"} className="px-1.5 py-1 text-indigo-500 hover:bg-indigo-100 border-l border-indigo-200"><Pencil className="h-3 w-3" /></button>
                              <button data-testid={`contract-preset-delete-${p.name}`} onClick={() => deletePreset(p)} title={zh ? "删除" : "Delete"} className="px-1.5 py-1 text-red-500 hover:bg-red-50 border-l border-indigo-200"><Trash2 className="h-3 w-3" /></button>
                            </>
                          )}
                        </span>
                      ))}
                    </div>
                    );
                  })()}
                  {(activeGroupObj?.options || []).map((o) => (
                    <div key={o.key} data-testid={`contract-addon-${o.key}`} className={`flex items-center gap-2 text-[13px] px-2 py-1.5 rounded-lg border ${addOns.includes(o.key) ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
                      <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
                        <input type="checkbox" checked={addOns.includes(o.key)} onChange={() => toggleAddon(o.key)} />
                        <span className={`truncate ${addOns.includes(o.key) ? "text-red-700" : "text-slate-600"}`}>{zh ? o.labelZh : o.labelEn}</span>
                      </label>
                      <input value={addonPrices[o.key] ?? o.price ?? ""} onChange={(e) => setPrice(o.key, e.target.value)} data-testid={`contract-addon-price-${o.key}`}
                        className="w-24 text-[12px] rounded-md border border-slate-200 px-2 py-1 text-right focus:border-brand-400 focus:outline-none" />
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-2 py-2 rounded-lg bg-brand-500 text-white" data-testid="contract-addon-subtotal">
                    <span className="text-[12px] font-medium">{zh ? "附加项合计 Add-ons Subtotal" : "Add-ons Subtotal"}</span>
                    <span className="text-sm font-bold" data-testid="contract-addon-subtotal-value">{money(addonSubtotal)}</span>
                  </div>
                  <label className={`flex items-center gap-2 px-2 py-2 rounded-lg border cursor-pointer text-[12px] ${fields.includeAddonsInTotal ? "border-red-300 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`} data-testid="contract-merge-addons-toggle">
                    <input type="checkbox" checked={!!fields.includeAddonsInTotal} onChange={(e) => setField("includeAddonsInTotal", e.target.checked)} data-testid="contract-merge-addons-checkbox" />
                    <span className="flex-1">{zh ? "附加项合计并入合同总价（付款计划按新总价重算）" : "Merge add-ons subtotal into total price (recalculates payment schedule)"}</span>
                    {fields.includeAddonsInTotal && addonSubtotal > 0 && <span className="font-bold" data-testid="contract-effective-total">= {auto.totalDisplay}</span>}
                  </label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid="contract-save-preset" onClick={savePreset} className="flex-1 h-8 border-indigo-200 text-indigo-700">{zh ? "存为模板" : "Save preset"}</Button>
                    <Button size="sm" variant="outline" data-testid="contract-save-std-prices" onClick={saveStdPrices} disabled={savingPrices || !pricesDirty} className="flex-1 h-8 border-slate-200">{savingPrices ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}{zh ? "保存标准单价" : "Save prices"}</Button>
                  </div>
                </div>
              ) : activeGroup !== "signature" && (
                <>
                  {activeGroup === "header" && (
                    <div className="flex items-center justify-between gap-2 rounded-lg bg-red-50 border border-red-100 px-2.5 py-1.5" data-testid="contract-header-hint">
                      <span className="text-[11px] text-red-700">{zh ? "项目名称可智能生成：客户名 · 城市 · ADU类型" : "Smart name: Owner · City · ADU type"}</span>
                      <Button size="sm" variant="outline" data-testid="contract-smart-name" onClick={smartName} className="h-7 border-red-200 text-red-700 shrink-0"><Sparkles className="h-3 w-3 mr-1" />{zh ? "智能命名" : "Smart name"}</Button>
                    </div>
                  )}
                  {activeGroup === "features" && (
                    <div className="rounded-lg bg-red-50 border border-red-100 px-2.5 py-2 space-y-1.5" data-testid="contract-features-hint">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-red-700 flex-1">{zh ? "1.1 项目特征可直接编辑；也可按面积重写，或一键带入报价明细（户型/层高/特殊配置）" : "Editable; regenerate from area or bring in estimate details"}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <Button size="sm" variant="outline" data-testid="contract-features-regen" onClick={regenFeatures} className="h-7 border-red-200 text-red-700"><RefreshCw className="h-3 w-3 mr-1" />{zh ? "按面积重新生成" : "Regenerate"}</Button>
                        <Button size="sm" variant="outline" data-testid="contract-features-from-estimate" onClick={applyEstimateFeatures} disabled={featBusy} className="h-7 border-indigo-200 text-indigo-700">{featBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}{zh ? "带入报价明细" : "From estimate"}</Button>
                        <Button size="sm" variant="outline" data-testid="contract-feature-save-preset" onClick={saveFeaturePreset} className="h-7 border-emerald-200 text-emerald-700"><Save className="h-3 w-3 mr-1" />{zh ? "存为模板" : "Save preset"}</Button>
                      </div>
                      {featPresets.length > 0 && (() => {
                        const own = featPresets.filter((p) => (p.branch || "") === pBranch);
                        const glob = pBranch ? featPresets.filter((p) => (p.branch || "") === "") : [];
                        return (
                        <div className="flex flex-wrap gap-1.5 items-center pt-1 border-t border-red-100" data-testid="contract-feature-presets">
                          <span className="text-[11px] text-slate-400 self-center">{zh ? "特征模板：" : "Presets:"}</span>
                          {glob.map((p) => (
                            <span key={`g-${p.name}`} data-testid={`contract-feature-preset-pill-${p.name}`} className="inline-flex items-center rounded-full bg-slate-50 border border-slate-200 overflow-hidden">
                              <button data-testid={`contract-feature-preset-${p.name}`} onClick={() => applyFeaturePreset(p)} className="text-[11px] px-2 py-1 text-slate-600 hover:bg-slate-100">{p.name}<span className="ml-1 text-[9px] text-slate-400">{zh ? "通用" : "Global"}</span></button>
                            </span>
                          ))}
                          {own.map((p) => (
                            <span key={`o-${p.name}`} data-testid={`contract-feature-preset-pill-${p.name}`} className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 overflow-hidden">
                              <button data-testid={`contract-feature-preset-${p.name}`} onClick={() => applyFeaturePreset(p)} className="text-[11px] px-2 py-1 text-emerald-700 hover:bg-emerald-100">{p.name}<span className="ml-1 text-[9px] text-emerald-400">{branchTag(pBranch)}</span></button>
                              {canManagePresets && (
                                <>
                                  <button data-testid={`contract-feature-preset-rename-${p.name}`} onClick={() => renameFeaturePreset(p)} title={zh ? "重命名" : "Rename"} className="px-1.5 py-1 text-emerald-500 hover:bg-emerald-100 border-l border-emerald-200"><Pencil className="h-3 w-3" /></button>
                                  <button data-testid={`contract-feature-preset-delete-${p.name}`} onClick={() => deleteFeaturePreset(p)} title={zh ? "删除" : "Delete"} className="px-1.5 py-1 text-red-500 hover:bg-red-50 border-l border-emerald-200"><Trash2 className="h-3 w-3" /></button>
                                </>
                              )}
                            </span>
                          ))}
                        </div>
                        );
                      })()}
                      <p className="text-[10.5px] text-slate-500">{zh ? "ℹ️ 此项目特征将同步显示在「工作范围 Scope of Works」文档中，保持信息一致。" : "ℹ️ Also synced into the Scope of Works document."}</p>
                    </div>
                  )}
                  {activeFields.map((fl) => (
                    <div key={fl.key}>
                      <label className="text-[12px] text-slate-600 block mb-0.5">{fl.label}</label>
                      {fl.type === "textarea" ? (
                        <textarea value={fields[fl.key] ?? ""} onChange={(e) => setField(fl.key, e.target.value)} data-testid={`contract-field-${fl.key}`} rows={4}
                          className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 focus:border-brand-400 focus:outline-none resize-y leading-relaxed" />
                      ) : fl.type === "select" ? (
                        <select value={fields[fl.key] ?? ""} onChange={(e) => setField(fl.key, e.target.value)} data-testid={`contract-field-${fl.key}`}
                          className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 bg-white focus:border-brand-400 focus:outline-none">
                          {(fl.options || []).map((o) => <option key={o.key} value={o.key}>{zh ? o.labelZh : o.labelEn}</option>)}
                        </select>
                      ) : (
                        <input type={fl.type === "date" ? "date" : "text"} value={fields[fl.key] ?? ""} onChange={(e) => setField(fl.key, e.target.value)} data-testid={`contract-field-${fl.key}`}
                          className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 focus:border-brand-400 focus:outline-none" />
                      )}
                    </div>
                  ))}
                </>
              )}
              {activeGroup !== "signature" && (AUTO_HINTS[activeGroup] || []).length > 0 && (
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1" data-testid="contract-auto-hints">
                  <p className="text-[11px] font-semibold text-slate-500">{zh ? "程序自动生成 (只读)" : "Auto-generated (read-only)"}</p>
                  {(AUTO_HINTS[activeGroup] || []).map(([k, lbl]) => (
                    <div key={k} className="flex justify-between text-[12px]"><span className="text-slate-500">{lbl}</span><span className="font-semibold text-red-600" data-testid={`contract-auto-${k}`}>{auto[k] || "—"}</span></div>
                  ))}
                </div>
              )}
              {activeGroup === "signature" && (
                <div className="space-y-3" data-testid="contract-signatures">
                  <p className="text-[12px] text-slate-500">{zh ? "手写签名将回填至合同签字区。业主也可用「业主签署」二维码在手机上签。" : "Signatures fill into the contract signature block."}</p>
                  <SignaturePad testid="contract-sig-owner" label={zh ? "业主签字 Owner" : "Owner"} value={signatures.owner} onChange={(v) => setSig("owner", v)} />
                  <SignaturePad testid="contract-sig-consultant" label={zh ? "建筑顾问签字 Consultant" : "Consultant"} value={signatures.consultant} onChange={(v) => setSig("consultant", v)} />
                  <SignaturePad testid="contract-sig-manager" label={zh ? "设计师经理签字 Manager" : "Manager"} value={signatures.manager} onChange={(v) => setSig("manager", v)} />
                </div>
              )}
            </div>
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="contract-sign-incentive">
                <p className="text-[12px] font-semibold text-amber-800">{zh ? "⏳ 签署优惠倒计时（业主端显示）" : "Signing incentive (shown to owner)"}</p>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-amber-700 whitespace-nowrap">{zh ? "自发送起" : "Within"}</span>
                  <input type="number" min="0" value={fields.signWithinDays ?? ""} onChange={(e) => setField("signWithinDays", e.target.value)} data-testid="contract-sign-within-days"
                    className="w-16 text-[12px] rounded-md border border-amber-200 px-2 py-1 text-right focus:border-amber-400 focus:outline-none" placeholder="7" />
                  <span className="text-[12px] text-amber-700 whitespace-nowrap">{zh ? "天内签署" : "days"}</span>
                </div>
                <input value={fields.signDiscountText ?? ""} onChange={(e) => setField("signDiscountText", e.target.value)} data-testid="contract-sign-discount-text"
                  className="w-full text-[12px] rounded-md border border-amber-200 px-2 py-1 focus:border-amber-400 focus:outline-none"
                  placeholder={zh ? "优惠说明，如：签署即享 $2,000 优惠" : "e.g. Sign now for $2,000 off"} />
                <p className="text-[11px] text-amber-600/80">{zh ? "填写后业主签署页会显示倒计时横幅，促成尽快签约。" : "Owner's sign page will show a countdown banner."}</p>
              </div>

              {(contractMeta.ownerViews && contractMeta.ownerViews.length > 0) && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="contract-view-trail">
                  <button onClick={() => setShowTrail((v) => !v)} data-testid="contract-view-trail-toggle" className="w-full flex items-center justify-between text-[12px] font-semibold text-blue-800">
                    <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{zh ? `业主查看轨迹（${contractMeta.ownerViews.length} 次）` : `Owner view trail (${contractMeta.ownerViews.length})`}</span>
                    <span className="text-blue-500">{showTrail ? "▲" : "▼"}</span>
                  </button>
                  {showTrail && (
                    <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {[...contractMeta.ownerViews].reverse().map((ts, i) => (
                        <li key={i} data-testid={`contract-view-trail-item-${i}`} className="flex items-center gap-2 text-[11px] text-blue-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                          {fmtDT(ts)}
                          {i === 0 && <span className="text-[10px] text-blue-400">{zh ? "最近" : "latest"}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="p-2 border-t border-slate-100">
              <Button size="sm" variant="outline" data-testid="contract-refresh-preview" onClick={saveRefresh} disabled={previewing} className="w-full h-8 border-slate-200">{previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}{zh ? "保存并刷新预览" : "Save & refresh preview"}</Button>
            </div>
          </div>

          <div className="flex-1 bg-slate-200 relative flex flex-col" data-testid="contract-preview">
            <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 text-slate-200 text-[12px]">
              <span>{zh ? "合同预览（与原 Word 版式一致）" : "Contract preview (matches original Word)"}</span>
              {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="contract-preview-newtab" className="inline-flex items-center gap-1 text-indigo-300 hover:text-white"><ExternalLink className="h-3.5 w-3.5" />{zh ? "在新标签打开" : "Open in tab"}</a>}
            </div>
            <div className="flex-1 relative">
              {previewing && <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10"><Loader2 className="h-7 w-7 animate-spin text-brand-500" /></div>}
              {pdfUrl ? <iframe title="contract-pdf" src={pdfUrl} className="w-full h-full" data-testid="contract-preview-iframe" />
                : <div className="w-full h-full flex items-center justify-center text-slate-400">{zh ? "预览生成中…" : "Generating…"}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
